// Mobile Walay product card: delegated add-to-cart and quick view for every card.
// Add to cart uses the same CartLinesUpdateEvent as the theme's own product form,
// so the cart drawer opens and the cart bubble updates exactly as on product pages.
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

// `Theme` is a top-level const in snippets/scripts.liquid (global lexical scope, not window).
// eslint-disable-next-line no-undef
const routes = (typeof Theme !== 'undefined' && Theme.routes) || {};
const root = (routes.root || '/').replace(/\/$/, '');

/* ---------------------------------------------------------------- cart */

async function addToCart(button, variantId = button.dataset.variantId, quantity = 1) {
  if (!variantId || button.classList.contains('is-loading')) return false;
  button.classList.add('is-loading');

  const formData = new FormData();
  formData.append('id', variantId);
  formData.append('quantity', String(quantity));

  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity }],
      promise: deferred.promise,
    })
  );

  const addUrl = routes.cart_add_url || '/cart/add.js';
  const cartUrl = (routes.cart_url || '/cart') + '.json';

  try {
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    }).then((r) => r.json());

    const ajaxCart = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((r) => r.json());

    if (response.status) {
      button.dispatchEvent(
        new CartErrorEvent({
          error: response.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: response.description, errors: response.errors },
        })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: { didError: true, items: ajaxCart.items, source: 'mw-product-card', itemCount: quantity },
      });
      showError(button, response.description || response.message || 'Could not add this item.');
      return false;
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: 'mw-product-card',
        sourceId: variantId,
        itemCount: quantity,
        sections: response.sections,
        didError: false,
      },
    });

    button.classList.add('is-added');
    setTimeout(() => button.classList.remove('is-added'), 1800);
    return true;
  } catch (error) {
    deferred.reject(error);
    console.warn('[mw-product-card] add to cart failed', error);
    showError(button, 'Something went wrong. Please try again.');
    return false;
  } finally {
    button.classList.remove('is-loading');
  }
}

function showError(button, message) {
  const box = button.closest('.mwqv')?.querySelector('[data-qv-error]');
  if (box) {
    box.textContent = message;
    box.hidden = false;
  } else {
    window.alert(message);
  }
}

/* ---------------------------------------------------------------- helpers */

let moneyPrefix = 'Rs.';

function money(cents) {
  const amount = cents / 100;
  const text = amount.toLocaleString('en-US', {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return moneyPrefix + text;
}

function sized(src, width) {
  if (!src) return '';
  const url = src.startsWith('//') ? 'https:' + src : src;
  return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
}

function htmlToText(html) {
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
}

const esc = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const productCache = new Map();

function fetchProduct(url) {
  const path = url.split('?')[0].split('#')[0] + '.js';
  if (!productCache.has(path)) {
    productCache.set(
      path,
      fetch(path, { headers: { Accept: 'application/json' } }).then((r) => {
        if (!r.ok) throw new Error('Product request failed');
        return r.json();
      })
    );
  }
  return productCache.get(path);
}

const isColorOption = (name) => /colou?r/i.test(name);

/* ---------------------------------------------------------------- quick view */

const ICONS = {
  close: '<path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>',
  prev: '<path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  next: '<path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
  shield:
    '<path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M9 12l2 2 4-4" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>',
  truck:
    '<path d="M3 6h11v9H3zM14 9h4l3 3v3h-7" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><circle cx="7" cy="17" r="1.8" fill="none" stroke="currentColor" stroke-width="1.8"/><circle cx="17" cy="17" r="1.8" fill="none" stroke="currentColor" stroke-width="1.8"/>',
  cash:
    '<rect x="3" y="6" width="18" height="12" rx="2" fill="none" stroke="currentColor" stroke-width="1.8"/><circle cx="12" cy="12" r="2.5" fill="none" stroke="currentColor" stroke-width="1.8"/>',
  badge:
    '<circle cx="12" cy="9" r="5" fill="none" stroke="currentColor" stroke-width="1.8"/><path d="M9 13.5L8 21l4-2 4 2-1-7.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>',
};
const icon = (name) => `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">${ICONS[name]}</svg>`;

let dialog;
let state = null;

function ensureStyles() {
  if (document.querySelector('link[data-mwqv-css]')) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = new URL('mw-quick-view.css', import.meta.url).href;
  link.dataset.mwqvCss = '';
  document.head.appendChild(link);
}

function buildDialog() {
  ensureStyles();
  dialog = document.createElement('dialog');
  dialog.className = 'mwqv';
  dialog.setAttribute('aria-labelledby', 'MwQvTitle');
  dialog.innerHTML = `
    <div class="mwqv__panel">
      <button type="button" class="mwqv__close" data-qv-close aria-label="Close quick view">${icon('close')}</button>
      <div class="mwqv__grid">
        <div class="mwqv__gallery">
          <div class="mwqv__stage">
            <div class="mwqv__badges" data-qv-badges></div>
            <img class="mwqv__image" data-qv-image alt="">
            <button type="button" class="mwqv__nav mwqv__nav--prev" data-qv-prev aria-label="Previous image">${icon('prev')}</button>
            <button type="button" class="mwqv__nav mwqv__nav--next" data-qv-next aria-label="Next image">${icon('next')}</button>
          </div>
          <div class="mwqv__thumbs" data-qv-thumbs role="list"></div>
        </div>
        <div class="mwqv__info">
          <div class="mwqv__scroll">
            <span class="mwqv__brand" data-qv-brand></span>
            <h2 class="mwqv__title" id="MwQvTitle" data-qv-title></h2>
            <div class="mwqv__meta" data-qv-meta></div>
            <div class="mwqv__price">
              <span class="mwqv__price-now" data-qv-price></span>
              <s class="mwqv__price-was" data-qv-compare></s>
              <span class="mwqv__save" data-qv-save></span>
            </div>
            <div class="mwqv__specs" data-qv-specs></div>
            <div class="mwqv__options" data-qv-options></div>
            <p class="mwqv__stock" data-qv-stock></p>
            <div class="mwqv__desc">
              <p data-qv-desc></p>
              <a class="mwqv__more" data-qv-more>Full details &amp; specs</a>
            </div>
            <ul class="mwqv__trust">
              <li>${icon('badge')}<span>100% authentic</span></li>
              <li>${icon('truck')}<span>Nationwide delivery</span></li>
              <li>${icon('cash')}<span>Cash on delivery</span></li>
            </ul>
          </div>
          <div class="mwqv__actions">
            <p class="mwqv__error" data-qv-error role="alert" hidden></p>
            <div class="mwqv__buy">
              <div class="mwqv__qty" data-qv-qty>
                <button type="button" data-qv-qty-minus aria-label="Decrease quantity">−</button>
                <input type="number" min="1" value="1" inputmode="numeric" aria-label="Quantity" data-qv-qty-input>
                <button type="button" data-qv-qty-plus aria-label="Increase quantity">+</button>
              </div>
              <button type="button" class="mwqv__btn mwqv__btn--add" data-qv-add>
                <span class="mwqv__btn-text">Add to cart</span>
                <span class="mwqv__btn-added" aria-hidden="true">Added ✓</span>
              </button>
            </div>
            <button type="button" class="mwqv__btn mwqv__btn--buy" data-qv-buy>Buy it now</button>
          </div>
        </div>
      </div>
      <div class="mwqv__loading" data-qv-loading><span></span></div>
    </div>`;
  document.body.appendChild(dialog);

  const q = (sel) => dialog.querySelector(sel);

  dialog.addEventListener('click', (event) => {
    const t = event.target instanceof Element ? event.target : null;
    if (!t) return;
    if (t === dialog || t.closest('[data-qv-close]')) return closeQuickView();
    if (t.closest('[data-qv-prev]')) return stepImage(-1);
    if (t.closest('[data-qv-next]')) return stepImage(1);
    const thumb = t.closest('[data-qv-thumb]');
    if (thumb) return showImage(Number(thumb.dataset.qvThumb));
    const value = t.closest('[data-qv-value]');
    if (value) return selectOption(Number(value.dataset.qvOption), value.dataset.qvValue);
    if (t.closest('[data-qv-qty-minus]')) return setQty(getQty() - 1);
    if (t.closest('[data-qv-qty-plus]')) return setQty(getQty() + 1);
  });

  q('[data-qv-qty-input]').addEventListener('change', () => setQty(getQty()));

  q('[data-qv-add]').addEventListener('click', async (event) => {
    event.stopPropagation();
    const variant = state?.variant;
    if (!variant?.available) return;
    q('[data-qv-error]').hidden = true;
    const ok = await addToCart(event.currentTarget, String(variant.id), getQty());
    if (ok) setTimeout(closeQuickView, 650);
  });

  q('[data-qv-buy]').addEventListener('click', () => {
    const variant = state?.variant;
    if (!variant?.available) return;
    window.location.href = `${root}/cart/${variant.id}:${getQty()}`;
  });

  dialog.addEventListener('keydown', (event) => {
    if (!(event.target instanceof Element) || event.target.closest('input')) return;
    if (event.key === 'ArrowLeft') stepImage(-1);
    if (event.key === 'ArrowRight') stepImage(1);
  });

  // Swipe between images on touch screens.
  let startX = null;
  const stage = q('.mwqv__stage');
  stage.addEventListener('touchstart', (e) => (startX = e.touches[0].clientX), { passive: true });
  stage.addEventListener(
    'touchend',
    (e) => {
      if (startX === null) return;
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 40) stepImage(dx < 0 ? 1 : -1);
      startX = null;
    },
    { passive: true }
  );

  dialog.addEventListener('close', () => {
    document.documentElement.classList.remove('mwqv-lock');
    state?.trigger?.focus({ preventScroll: true });
    state = null;
  });
}

const getQty = () => Math.max(1, parseInt(dialog.querySelector('[data-qv-qty-input]').value, 10) || 1);
const setQty = (n) => (dialog.querySelector('[data-qv-qty-input]').value = String(Math.max(1, Math.min(99, n))));

function closeQuickView() {
  if (!dialog?.open) return;
  dialog.classList.add('is-closing');
  setTimeout(() => {
    dialog.classList.remove('is-closing');
    dialog.close();
  }, 180);
}

/* gallery */

function renderGallery() {
  const { images } = state;
  const thumbs = dialog.querySelector('[data-qv-thumbs]');
  thumbs.innerHTML = images
    .map(
      (src, i) =>
        `<button type="button" class="mwqv__thumb" role="listitem" data-qv-thumb="${i}" aria-label="Image ${i + 1}"><img src="${esc(
          sized(src, 160)
        )}" alt="" loading="lazy"></button>`
    )
    .join('');
  thumbs.hidden = images.length < 2;
  dialog.querySelectorAll('[data-qv-prev], [data-qv-next]').forEach((b) => (b.hidden = images.length < 2));
}

function showImage(index) {
  const { images } = state;
  if (!images.length) return;
  state.imageIndex = (index + images.length) % images.length;
  const img = dialog.querySelector('[data-qv-image]');
  img.classList.remove('is-in');
  img.src = sized(images[state.imageIndex], 900);
  img.alt = state.product.title;
  requestAnimationFrame(() => img.classList.add('is-in'));
  dialog.querySelectorAll('[data-qv-thumb]').forEach((t, i) => {
    t.classList.toggle('is-active', i === state.imageIndex);
    if (i === state.imageIndex) t.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  });
}

const stepImage = (dir) => state && showImage(state.imageIndex + dir);

/* variants */

function findVariant(selected) {
  return state.product.variants.find((v) => v.options.every((value, i) => value === selected[i]));
}

function selectOption(optionIndex, value) {
  const selected = [...state.selected];
  selected[optionIndex] = value;
  let variant = findVariant(selected);
  // If that combination doesn't exist, pick the closest available one with this value.
  if (!variant) {
    variant =
      state.product.variants.find((v) => v.options[optionIndex] === value && v.available) ||
      state.product.variants.find((v) => v.options[optionIndex] === value);
  }
  if (variant) setVariant(variant);
}

function setVariant(variant) {
  state.variant = variant;
  state.selected = [...variant.options];
  renderOptions();
  renderPrice();

  const src = variant.featured_image?.src;
  if (src) {
    const index = state.images.findIndex((img) => img.split('?')[0].endsWith(src.split('?')[0].split('/').pop()));
    if (index >= 0) showImage(index);
  }
}

function renderOptions() {
  const { product, selected } = state;
  const wrap = dialog.querySelector('[data-qv-options]');
  if (product.variants.length < 2) {
    wrap.innerHTML = '';
    return;
  }

  wrap.innerHTML = product.options
    .map((option, oi) => {
      const name = option.name || option;
      const values = option.values || [...new Set(product.variants.map((v) => v.options[oi]))];
      const color = isColorOption(name);
      const buttons = values
        .map((value) => {
          const combo = [...selected];
          combo[oi] = value;
          const match = findVariant(combo);
          const available = match ? match.available : product.variants.some((v) => v.options[oi] === value && v.available);
          const active = selected[oi] === value;
          let swatch = '';
          if (color) {
            const withImage = product.variants.find((v) => v.options[oi] === value && v.featured_image);
            if (withImage) swatch = `<img src="${esc(sized(withImage.featured_image.src, 80))}" alt="" loading="lazy">`;
          }
          return `<button type="button" class="mwqv__value${active ? ' is-active' : ''}${available ? '' : ' is-unavailable'}${
            swatch ? ' has-swatch' : ''
          }" data-qv-option="${oi}" data-qv-value="${esc(value)}" aria-pressed="${active}"${
            available ? '' : ` title="${esc(state.soldLabel)}"`
          }>${swatch}<span>${esc(value)}</span></button>`;
        })
        .join('');
      return `<fieldset class="mwqv__option"><legend>${esc(name)}: <strong>${esc(selected[oi])}</strong></legend><div class="mwqv__values">${buttons}</div></fieldset>`;
    })
    .join('');
}

function renderPrice() {
  const { variant } = state;
  const q = (sel) => dialog.querySelector(sel);
  const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

  const price = q('[data-qv-price]');
  price.textContent = money(variant.price);
  price.classList.toggle('is-sale', Boolean(onSale));
  q('[data-qv-compare]').textContent = onSale ? money(variant.compare_at_price) : '';
  const save = q('[data-qv-save]');
  save.textContent = onSale ? `Save ${money(variant.compare_at_price - variant.price)}` : '';
  save.hidden = !onSale;

  const stock = q('[data-qv-stock]');
  stock.textContent = variant.available ? 'In stock, ready to ship' : state.soldLabel;
  stock.classList.toggle('is-out', !variant.available);

  const add = q('[data-qv-add]');
  add.disabled = !variant.available;
  add.querySelector('.mwqv__btn-text').textContent = variant.available ? state.addLabel : state.soldLabel;
  q('[data-qv-buy]').hidden = !variant.available;
  q('[data-qv-qty]').hidden = !variant.available;
}

/* open */

async function openQuickView(trigger) {
  if (!dialog) buildDialog();
  const d = trigger.dataset;
  const card = trigger.closest('[data-mw-card]');
  const q = (sel) => dialog.querySelector(sel);

  const prefix = (d.price || '').match(/^[^\d]*/);
  if (prefix && prefix[0]) moneyPrefix = prefix[0].replace(/\s+$/, '') + (prefix[0].endsWith(' ') ? ' ' : '');

  state = { trigger, images: d.image ? [d.image] : [], imageIndex: 0, addLabel: d.addLabel || 'Add to cart', soldLabel: d.soldLabel || 'Sold out' };

  // Paint instantly from the card, then fill in variants and the gallery.
  q('[data-qv-brand]').textContent = d.brand || '';
  q('[data-qv-title]').textContent = d.title || '';
  q('[data-qv-price]').textContent = d.price || '';
  q('[data-qv-compare]').textContent = d.compare || '';
  q('[data-qv-save]').hidden = true;
  q('[data-qv-desc]').textContent = '';
  q('[data-qv-options]').innerHTML = '';
  q('[data-qv-stock]').textContent = '';
  q('[data-qv-error]').hidden = true;
  q('[data-qv-more]').href = d.url || '#';
  setQty(1);

  const specs = (d.subtitle || '').split('·').map((s) => s.trim()).filter(Boolean);
  q('[data-qv-specs]').innerHTML = specs.map((s) => `<span>${esc(s)}</span>`).join('');

  const meta = [];
  const rating = card?.querySelector('.mw-card__rating');
  if (rating) meta.push(`<span class="mwqv__rating">${rating.innerHTML}</span>`);
  if (card?.querySelector('.mw-card__pta')) meta.push(`<span class="mwqv__pta">${icon('shield')}PTA approved</span>`);
  q('[data-qv-meta]').innerHTML = meta.join('');
  q('[data-qv-meta]').hidden = !meta.length;

  const badges = card ? [...card.querySelectorAll('.mw-card__badges .mw-card__badge')].map((b) => b.outerHTML).join('') : '';
  q('[data-qv-badges]').innerHTML = badges;

  const img = q('[data-qv-image]');
  img.src = d.image || '';
  img.alt = d.title || '';
  q('[data-qv-thumbs]').innerHTML = '';
  dialog.querySelectorAll('[data-qv-prev], [data-qv-next]').forEach((b) => (b.hidden = true));
  q('[data-qv-add]').disabled = true;

  dialog.classList.add('is-loading');
  document.documentElement.classList.add('mwqv-lock');
  dialog.showModal();
  q('[data-qv-close]').focus({ preventScroll: true });

  try {
    const product = await fetchProduct(d.url || '');
    if (state?.trigger !== trigger) return;
    state.product = product;
    state.images = (product.images || []).length ? product.images : state.images;

    const text = htmlToText(product.description);
    q('[data-qv-desc]').textContent = text.length > 320 ? text.slice(0, 320).replace(/\s+\S*$/, '') + '…' : text;

    renderGallery();
    showImage(0);
    const start =
      product.variants.find((v) => String(v.id) === d.variantId) ||
      product.variants.find((v) => v.available) ||
      product.variants[0];
    setVariant(start);
  } catch (error) {
    console.warn('[mw-product-card] quick view failed', error);
    window.location.href = d.url;
  } finally {
    dialog.classList.remove('is-loading');
  }
}

/* ---------------------------------------------------------------- events */

if (!window.__mwCardBound) {
  window.__mwCardBound = true;

  document.addEventListener('click', async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const quick = target.closest('[data-mw-quick-view]');
    if (quick) {
      event.preventDefault();
      openQuickView(quick);
      return;
    }

    const add = target.closest('[data-mw-add]');
    if (add) {
      event.preventDefault();
      await addToCart(add);
    }
  });

  // Warm the product JSON when a shopper hovers the eye button.
  document.addEventListener(
    'pointerover',
    (event) => {
      const quick = event.target instanceof Element && event.target.closest('[data-mw-quick-view]');
      if (quick?.dataset.url) fetchProduct(quick.dataset.url).catch(() => {});
    },
    { passive: true }
  );
}
